<template>
    <v-card class="card_tile" v-if="displayTile && contentSearchObj && ypoSearchText">
        <v-flex class="card_tile__border">
            <span class="ypo-search-title">{{contentTileConstants.TITLE}}</span>
            <v-flex class="ypo-search-open-full-directory">
                <small class="contentRedirect_unitTest" @click="openContentSearchPage">{{contentTileConstants.FULL_CONTENT_DIRECTORY}}</small>
            </v-flex>
            <v-flex>
                <status-overlay content-height="200px" :action="loadingAction"></status-overlay>
                <v-layout v-if="hasSuggestionResults" class="result_block" column>
                    <v-flex @click="openContent(content)" class="result_block__row"
                            v-for="(content, index) in contentSearchObj.searchResults"
                            :key="index.id">
                        <v-flex class="result_block__row__image">
                            <img class="result_block__row__image__img"
                                 :src="content.contentImageURL"
                                 :key="content.contentImageURL"
                                 :alt="content.contentType"
                                 :onerror="imageErrorFallback(content.contentFallBackImageURL)"
                            />
                        </v-flex>
                        <v-flex xs12 class="result_block__info">
                            <p class="result_block__info__contentName">{{content.contentName}}</p>
                            <p class="result_block__info__date">
                                {{getShortDate(new Date(content.date))}}
                            </p>
                            <small>{{content.communityName}}</small>
                        </v-flex>
                    </v-flex>
                </v-layout>
                <v-layout class="one-click-search-results" v-if="showFacets && showFacets.length && searchCompleted" column>
                    <v-flex class="one-click-search-results__item" v-for="(facet, index) in showFacets"
                            :key="index">
                        <span v-if="facetOtherLabel(facet, globalSearchConstants.OTHER_LABEL_IN)" class="one-click-search-results__item--other-label">{{facetOtherLabel(facet, globalSearchConstants.OTHER_LABEL_IN)}}&nbsp;</span>
                        <span @click="createAFilterPill(facet)" class="pointer one-click-search-results__item--label"><strong>{{ facet.name }}</strong></span>
                        <span v-if="facetOtherLabel(facet, globalSearchConstants.OTHER_LABEL_BEGINS_WITH)" class="one-click-search-results__item--other-label">&nbsp;{{facetOtherLabel(facet, globalSearchConstants.OTHER_LABEL_BEGINS_WITH)}}</span>
                    </v-flex>
                </v-layout>
                <v-layout v-if="!hasNoResults && searchCompleted" class="ypo-search-all-results">
                    <v-flex class="ypo-search-all-results__block">
                        <span class="ypo-search-all-results__view-all">{{globalSearchConstants.VIEW}} </span>
                        <span class="ypo-search-all-results__all-matching" @click="openAllMatchingContent">{{globalSearchConstants.ALL_MATCHING_RESULTS}}</span>
                    </v-flex>
                </v-layout>
                <v-layout v-if="hasNoResults && searchCompleted">
                    <no-results-available
                            :message="globalSearchConstants.NO_CONTENT_RESULTS_AVAILABLE"></no-results-available>
                </v-layout>
            </v-flex>
        </v-flex>
    </v-card>
</template>

<script>
    import { imageErrorFallback } from "../../common/utils/utils";
    import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
    import { storeGetters } from "../../store/storeGetters.constant";
    import { storeAction } from "../../../src/store/storeActions.constant";
    import { appConstant } from "../../app.constant";
    import {
        contentTileConstants,
        globalSearchConstants,
        facetLabel,
        constructSortFilters,
    } from "./ypoSearch.constants";
    import { routerNames } from "../appCommon.constants";
    import { getShortDate } from "../../common/utils/dateTime";
    import { handleSelectedContent } from "../ContentDirectoryPage/contentDirectoryPage.constant";
    import NoResultsAvailable from "./NoResultsAvailable";
    import StatusOverlay from '../StatusOverlay/StatusOverlay';
    import { storeMutation } from "@/store/storeMutations.constant";
    import { gaDataLayerEvent } from "@/plugins/analytics";
    import { gtmEventName } from "@/plugins/gtmAnalytics";

    export default {
        name: 'ContentTile',
        data: () => ({
            globalSearchConstants,
            contentTileConstants,
            displayTile: true,
            getShortDate,
            appConstant,
            routerNames,
        }),
        props: {
            contentSearchObj: {
                required: true,
                type: Object,
            },
            ypoSearchText: {
                type: String,
                required: true,
            },
            postSelectCallback: {
                type: Function,
                required: true,
            },
            searchCompleted: {
                type: Boolean,
                required: true,
            },
        },
        components: {
            NoResultsAvailable,
            StatusOverlay,
        },
        methods: {
            ...mapActions({
                getDownloadFolders: `chapter/${storeAction.getDownloadFolders}`,
                getContentSearchSuggestions: `ypoSearch/${storeAction.getContentSearchSuggestions}`,
                initiateTracking: `ypoSearch/${storeAction.initiateTracking}`,
            }),
            ...mapMutations({
                setContentSelectedLetterFilter: `contentDirectory/${storeMutation.setContentSelectedLetterFilter}`,
            }),
            imageErrorFallback,
            facetLabel,
            facetOtherLabel(facet, expectedLabel) {
                return facet.integratedSearchName === expectedLabel ? expectedLabel : "";
            },
            openAllMatchingContent() {
                if (this.postSelectCallback) this.postSelectCallback();
                const trackingObject = {
                    committedSearchTerm: this.ypoSearchText,
                    oneClickSearchText: globalSearchConstants.ALL_MATCHING_RESULTS,
                    oneClickSearchType: globalSearchConstants.CONTENT,
                };
                sessionStorage.removeItem(appConstant.storageKeys.contentDirectoryStoredFilters);
                this.setContentSelectedLetterFilter();

                this.$router.push({
                    path: `/${routerNames.CONTENT_DIRECTORY_PAGE}`,
                    query: {
                        contentSearchText: this.ypoSearchText ? this.ypoSearchText : '',
                    },
                });
                this.initiateTracking(trackingObject);
                gaDataLayerEvent(gtmEventName.C_Content_Search_Trigger, globalSearchConstants.ALL_MATCHING_RESULTS);
            },
            createAFilterPill(facet) {
                if (this.postSelectCallback) this.postSelectCallback();
                const trackingObject = {
                    committedSearchTerm: this.ypoSearchText,
                    oneClickSearchText: this.showFacets ? facet.name : null,
                    oneClickSearchType: globalSearchConstants.CONTENT,
                };
                const {
                    label,
                    filterValue,
                    ribbonGroupName,
                } = facet.filters[0];
                const {
                    key,
                    name,
                    fieldToQuery,
                    integratedSearchName,
                } = facet;

                const pillFilter = {
                    ...facet.filters[0],
                    key,
                    name,
                    filterValue,
                    fieldToQuery,
                    ribbonGroupName,
                    integratedSearchName,
                    label: this.facetLabel(label, facet.integratedSearchName, this.ypoSearchText),

                };

                sessionStorage.removeItem(appConstant.storageKeys.contentDirectoryStoredFilters);
                this.setContentSelectedLetterFilter();

                this.$router.push({
                    path: `/${routerNames.CONTENT_DIRECTORY_PAGE}`,
                    query: {
                        contentFilters: JSON.stringify({
                            contentType: [pillFilter],
                        }),
                        contentSearchText: this.ypoSearchText ? this.ypoSearchText : '',
                    },
                });
                this.initiateTracking(trackingObject);
                gaDataLayerEvent(gtmEventName.C_Content_Search_Trigger, facet.name);
            },
            openContentSearchPage() {
                if (this.postSelectCallback) this.postSelectCallback();
                const trackingObject = {
                    committedSearchTerm: this.ypoSearchText,
                    oneClickSearchText: contentTileConstants.FULL_CONTENT_DIRECTORY,
                    oneClickSearchType: globalSearchConstants.CONTENT,
                };

                sessionStorage.removeItem(appConstant.storageKeys.contentDirectoryStoredFilters);
                this.setContentSelectedLetterFilter();

                this.$router.push({
                    path: `/${routerNames.CONTENT_DIRECTORY_PAGE}`,
                });
                this.initiateTracking(trackingObject);
                gaDataLayerEvent(gtmEventName.C_Content_Search_Trigger, contentTileConstants.FULL_CONTENT_DIRECTORY);
            },
            openContent(content) {
                if (this.postSelectCallback) this.postSelectCallback();
                const trackingObject = {
                    committedSearchTerm: this.ypoSearchText,
                    tileText: this.hasSuggestionResults ? content.contentName : null,
                    tileType: globalSearchConstants.CONTENT,
                };
                handleSelectedContent(content, this.ypoSearchText, this.$router, this.getDownloadFolders, this.isMobileAppBrowser);
                this.initiateTracking(trackingObject);
            },
        },
        computed: {
            ...mapState('actionState', ['actions']),
            ...mapGetters({
                isMobileAppBrowser: `${[storeGetters.isMobileAppBrowser]}`,
            }),
            loadingAction() {
                return this.actions[storeAction.getContentSearchSuggestions];
            },
            hasSuggestionResults() {
                return this.contentSearchObj &&
                    this.contentSearchObj.searchResults &&
                    this.contentSearchObj.searchResults.length > 0;
            },
            showFacets() {
                let facets = [];
                const hasValidContentSearchResults =
                    this.contentSearchObj &&
                    this.contentSearchObj.searchResults
                    && this.contentSearchObj.searchResults.length > 0;
                const hasValidContentRibbonGroup =
                    this.contentSearchObj.searchRibbonGroup &&
                    this.contentSearchObj.searchRibbonGroup.length > 0;

                if (hasValidContentSearchResults) {
                    if (hasValidContentRibbonGroup) {
                        facets = this.contentSearchObj.searchRibbonGroup;
                    }
                }
                return facets;
            },
            hasNoResults() {
                return this.contentSearchObj
                    && this.contentSearchObj.searchResults &&
                    this.contentSearchObj.searchResults.length <= 0;
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import "../../common/styles/_colors.scss";
    @import "../../common/styles/_responsive.scss";

    .card_tile {
        text-align: left !important;
        padding: 20px 0 20px 20px;
        box-shadow: none;
        height: 100vh;
        @include breakpoint(md) {
            height: unset !important;
        }

        @include breakpoint(xs) {
            border-top: 4px solid $ypo-tile-border;
        }

        &__border {
            height: 100vh;
            border-right: 1px solid $ypo-tile-border;
            @include breakpoint(xs) {
                border-right: none;

            }
            @include breakpoint(sm) {
                height: unset !important;
            }
        }
    }

    .divider {
        color: $black;
        font-weight: 500;
    }

    .one-click-search-results {
        flex-direction: column;
        @include breakpoint(sm) {
            flex-direction: row;
            flex-wrap: wrap;
        }

        &__item {
            @include breakpoint(sm) {
                border: 1px solid $ypo-cta;
                box-shadow: 1px 1px 3px 0 rgba(0, 0, 0, 0.2);
                margin: 6px 3px;
                padding: 6px;
                max-width: fit-content !important;
                height: 36px;
            }
            padding: 6px 15px 6px 0;
            font-family: 'Raleway', sans-serif;

            &--other-label {
                color: $black;
                font-size: 12px;
                font-family: "Noto Sans", sans-serif;
            }

            &--label {
                font-size: 14px;
                color: $ypo-cta;
                text-decoration: none;
            }
        }
    }

    .result_block {
        position: relative;
        border-bottom: 1px solid $ypo-tile-border;
        margin-right: 20px;
        margin-bottom: 10px;

        &__row {
            position: relative;
            padding-left: 60px;
            margin-bottom: 10px;
            min-height: 70px;
            align-items: center;
            display: inherit;
            transition: 0.3ms all ease;
            cursor: pointer;

            &:hover {
                background: $ypo-extra-light-gray;
            }

            &__image {
                position: absolute;
                left: 0;

                &__img {
                    height: 48px;
                    width: 48px;
                    border-radius: 20%;
                    overflow: hidden;
                    display: block;
                    font-size: 8px;
                }
            }
        }

        &__info {

            &__contentName {
                display: block;
                display: -webkit-box;
                color: $ypo-cta;
                font-size: 14px;
                margin: 0;
                font-weight: bold;
                font-family: 'Raleway', sans-serif;
                letter-spacing: 0.22px;
                line-height: 20px;
                text-overflow: ellipsis;
                overflow: hidden;
                cursor: pointer;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;

            }

            &__date {
                font-weight: bold !important;
                color: $ypo-dark-gray !important;
                font-size: 12px !important;
                margin: 0;
                line-height: 16px;
                font-family: 'Noto Sans', sans-serif;
            }

            a {
                text-decoration: none;
            }


            small {
                display: block;
                color: $black;
                font-size: 12px;
                letter-spacing: -0.1px;
                line-height: 16px;
                text-overflow: ellipsis;
                overflow: hidden;
                cursor: pointer;
                font-family: 'Noto Sans', sans-serif;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                display: -webkit-box;
            }

        }
    }

</style>
